import React from 'react';
import Stack from 'react-bootstrap/Stack';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { isAddress } from 'ethers';

import LoginModal from './LoginModal';
import LoadingButton from '../Widgets/LoadingButton';
import ToastMessage from '../Widgets/ToastMessage';
import AntelopeTokenAPI from '../Helpers/AntelopeTokenAPI';
import EVMTokenAPI from '../Helpers/EVMTokenAPI';
import TokenDetails from '../Helpers/TokenDetails';
import commonContextsWrapper from '../Helpers/commonContextsWrapper';

import './CreateTokenForm.css';

class CreateTokenForm extends React.Component {
    #MAX_PRECISION = 18;

    get #MAX_SUPPLY() {
        return this.state.tokenAPI?.constructor.MAX_TOKEN_SUPPLY;
    }

    get sharedState() {
        return this.props.sharedState?.[0];
    }

    get isLoggedIn() {
        return this.props.sharedState?.[1]?.isLoggedIn;
    }

    get isRunningOnEVM() {
        return !!this.sharedState?.evmSession;
    }

    get updateSharedState() {
        return this.props.sharedState?.[1]?.updateState;
    }

    constructor(props) {
        super(props);
        
        this.state = {
            isTransacting: false,
            isLoading: false,
            tokenAPI: undefined,
            errorMessage: undefined
        };

        this.createTokenFormSubmitted = this.createTokenFormSubmitted.bind(this);
        this.setTokenSymbolValidityMessage = this.setTokenSymbolValidityMessage.bind(this);
        this.setPrecisionValidityMessage = this.setPrecisionValidityMessage.bind(this);
        this.setEVMTokenContractAddress = this.setEVMTokenContractAddress.bind(this);
        this.hideErrorMessage = this.hideErrorMessage.bind(this);
    };

    // Action
    createTokenFormSubmitted(event) {
        event.preventDefault();

        const form = event.target;
        const name = (form['name'].value.trim() || null);
        const maxSupplyRaw = (form['maxSupply'].value || undefined); // Defaults to unlimited supply
        const precision = parseInt(form['precision'].value || 0);
        const symbol = form['tokenSymbol'].value.trim().toUpperCase();

        if (maxSupplyRaw && !this.isMaxSupplyValid(maxSupplyRaw, precision)) {
            this.setState({errorMessage: 'Too large number. Adjust the max supply or the precision.'});
            return;
        }

        // Defaults to the MAX possible if not entered
        const maxSupply = maxSupplyRaw ? (BigInt(maxSupplyRaw) * (10n ** BigInt(precision))) : this.#MAX_SUPPLY;

        this.setState({isTransacting: true});

        this.state.tokenAPI.createToken(name, maxSupply, precision, symbol)
            .then(({trxHash, contractAddress}) => {
                const token = new TokenDetails({name, maxSupply, precision, symbol, contractAddress});

                if (this.isRunningOnEVM) {
                    this.props.onEVMTokenCreated(trxHash, contractAddress);
                }
                else {
                    this.props.onAntelopeTokenCreated(trxHash);
                }
                
                this.updateSharedState({token});
            })
            .catch(error => this.showErrorMessage(error))
            .finally(() => this.setState({isTransacting: false}));
    };

    // Form Input
    setMaxSupplyValidityMessage(event) {
        const field = event.target;
        const validity = field.validity;
        
        if (!validity.valid && validity.patternMismatch) {
            field.setCustomValidity('Use a positive whole number or leave the field empty for an unlimited supply.')
        }
        else {
            field.setCustomValidity('');
        }
    };

    setTokenSymbolValidityMessage(event) {
        const field = event.target;
        const validity = field.validity;

        if (!validity.valid && validity.patternMismatch) {
            field.setCustomValidity('Use only A-Z characters.')
        }
        else {
            field.setCustomValidity('');
        }
    };

    setPrecisionValidityMessage(event) {
        const field = event.target;
        const validity = field.validity;

        if (!validity.valid && validity.patternMismatch) {
            field.setCustomValidity('Use only range between 0 and 18.');
        }
        else {
            field.setCustomValidity('');
        }
    };

    // Error Message
    showErrorMessage(error) {
        if (error.message.includes('Modal closed') || error.code === 'ACTION_REJECTED') {
            return;
        }

        const internalError = error.info?.error;
        const errorMessage = (internalError?.data?.message || internalError?.message || error.message);
        
        this.setState({errorMessage: errorMessage});
    };

    hideErrorMessage() {
        this.setState({errorMessage: undefined});
    };

    // Misc
    isMaxSupplyValid(maxSupplyRaw, precision) {
        try {
            const maxSupply = BigInt(maxSupplyRaw);
            const realMaxSupply = (maxSupply * (10n ** BigInt(precision)));
            return (realMaxSupply <= this.#MAX_SUPPLY);
        }
        catch (error) {
            return false;
        }
    };

    setLoginModalShown(isShown) {
        this.setState({isLoginModalShown: isShown});
    };

    setEVMTokenContractAddress() {
        const contractAddress = prompt('Enter address of your token');

        if (isAddress(contractAddress)) {
            this.setState({isLoading: true});
            
            this.state.tokenAPI
                .fetchTokenDetails(contractAddress)
                .then(token => this.updateSharedState({token}))
                .catch(error => alert(error))
                .finally(() => this.setState({isLoading: false}))
        }
    };

    // React
    componentDidUpdate() {
        if (this.isLoggedIn && !this.state.tokenAPI) {
            let tokenAPI;

            if (this.isRunningOnEVM) {
                tokenAPI = new EVMTokenAPI(this.sharedState.evmSession);
            }
            else {
                tokenAPI = new AntelopeTokenAPI(this.sharedState.antelopeSession);
            }

            this.setState({tokenAPI});
        }
    };

    render() {
        const isTransacting = this.state.isTransacting;
        const isLoading = this.state.isLoading;
        const isLoggedIn = this.isLoggedIn;
        const isRunningOnEVM = this.isRunningOnEVM;
        const accountAddress = (this.sharedState?.evmSession?.address || this.sharedState?.antelopeSession?.actor.toString());

        return (
            <>
                {/* Modals */}
                <LoginModal
                    isShown={this.state.isLoginModalShown}
                    onCloseClicked={() => this.setLoginModalShown(false)}
                />

                {/* ToastMessage */}
                <ToastMessage.Error
                    isShown={!!this.state.errorMessage}
                    message={this.state.errorMessage}
                    onAutoClosed={this.hideErrorMessage}
                />

                {/* Main Content */}
                <header>
                    <h2>Create a Token</h2>
                    <h6 className="FormHeaderSubtitle">Create your own token on Telos</h6>
                </header>

                <Stack className="CreateTokenForm-contentStack">
                    <Form id="createTokenForm" onSubmit={this.createTokenFormSubmitted}>
                        <Form.Group className="mb-3" controlId="name">
                            <Form.Label>Name</Form.Label>
                            <Form.Control type="text" placeholder="Name" maxLength="50" disabled={isTransacting}/>
                            <Form.Text className="text-muted">Token's name. If left empty then the symbol will be used as a default.</Form.Text>
                        </Form.Group>

                        <Row>
                            <Form.Group as={Col} className="mb-3" controlId="maxSupply">
                                <Form.Label>Max Supply</Form.Label>
                                <Form.Control type="text" placeholder="Unlimited" maxLength="19" pattern="([1-9][0-9]{0,18})" onChange={this.setMaxSupplyValidityMessage} disabled={isTransacting}/>
                                <Form.Text className="text-muted">Leave empty for an unlimited supply.</Form.Text>
                            </Form.Group>

                            <Form.Group as={Col} className="mb-3" controlId="tokenSymbol">
                                <Form.Label>Token's Symbol</Form.Label>
                                <Form.Control type="text" placeholder="BANANA" maxLength="7" style={{textTransform: 'uppercase'}} pattern="[a-zA-Z]{1,7}" onChange={this.setTokenSymbolValidityMessage} disabled={isTransacting} required/>
                                <Form.Text className="text-muted">Max 7 chars, letters only.</Form.Text>
                            </Form.Group>
                        </Row>

                        <Form.Group className="mb-3" controlId="precision">
                            <Form.Label>Precision</Form.Label>
                            <Form.Control type="number" placeholder="Decimal Places" min="0" max={this.#MAX_PRECISION} onChange={this.setPrecisionValidityMessage} disabled={isTransacting}/>
                            <Form.Text className="text-muted">Between 0-{this.#MAX_PRECISION} decimals specifying the minimum divisible unit.</Form.Text>
                        </Form.Group>

                        { isLoggedIn &&
                            <Form.Group className="mb-3">
                                <Form.Label>Issuer</Form.Label>
                                <Form.Control type="text" value={accountAddress} disabled={true}/>
                                <Form.Text className="text-muted blue pointerCursor" onClick={this.setEVMTokenContractAddress}>Have an existing token already?</Form.Text>
                            </Form.Group>
                        }
                    </Form>

                    { isLoggedIn &&
                        <>
                            <LoadingButton className="actionButton" type="submit" form="createTokenForm" variant="primary" isLoading={isTransacting || isLoading}>
                                Create
                            </LoadingButton>

                            { !isRunningOnEVM &&
                                <p className="feeMessage">Creating a token costs a humble 10 TLOS</p>
                            }
                        </>
                    }

                    { !isLoggedIn &&
                        <Button className="actionButton" variant="primary" onClick={() => this.setLoginModalShown(true)}>Login</Button>
                    }
                </Stack>
            </>
        )
    };
}

export default commonContextsWrapper(CreateTokenForm);
